import { AxiosResponse } from 'axios';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../api';
import useThrowAsyncError from '../hooks/useThrowAsyncError';
import IdentityContext from './IdentityContext';
import { getLicenseTransfers, LicenseTransfer } from '../pages/my-profile/api/licenses';

export type UserInvite = {
  id: string;
  name: string;
  owner: {
    id: string;
    firstName: string;
    lastName: string;
  };
  school: {
    id: string;
    name: string;
    isSmartschool: boolean;
    cities?: string;
  };
  schoolYear: string;
  subjects: string[];
};

type InvitationContextValue = {
  pendingInvitations: UserInvite[];
  handleInvitation: (invitationId: string) => void;
  fetchInvitations: () => Promise<void>;
  licenseTransfers: LicenseTransfer[];
  refetchLicenseTransfers: () => Promise<void>;
};

export const defaultInvitationContextValue: InvitationContextValue = {
  pendingInvitations: [],
  handleInvitation: () => {},
  fetchInvitations: () => Promise.resolve(),
  licenseTransfers: [],
  refetchLicenseTransfers: () => Promise.resolve(),
};

const InvitationContext = createContext<InvitationContextValue>(defaultInvitationContextValue);

export default InvitationContext;

export const InvitationContextProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const { REACT_APP_P_STUDIO_GROUP } = process.env;

  const [pendingInvitations, setPendingInvitations] = useState<UserInvite[]>([]);
  const [licenseTransfers, setLicenseTransfers] = useState<LicenseTransfer[]>([]);

  const { identity } = useContext(IdentityContext);
  const handleError = useThrowAsyncError();

  const handleInvitation = useCallback(
    (invitationId: string) => {
      setPendingInvitations(pendingInvitations.filter(invitation => invitation.id !== invitationId));
    },
    [pendingInvitations],
  );

  const fetchUserInvites = useCallback(async () => {
    try {
      const {
        data: { data: userInvites },
      } = await api.get<AxiosResponse<UserInvite[]>>('/studio/user/invites');

      setPendingInvitations(userInvites);
    } catch (err) {
      handleError(err);
    }
  }, [handleError]);

  const refetchLicenseTransfers = useCallback(async () => {
    try {
      const transfers = await getLicenseTransfers();
      setLicenseTransfers(transfers);
    } catch (error) {
      handleError(error);
    }
  }, [handleError]);

  useEffect(() => {
    if (!identity.isAuthenticated) return;

    if (['SO', 'ES'].includes(REACT_APP_P_STUDIO_GROUP)) {
      fetchUserInvites();
    }
    refetchLicenseTransfers();
  }, [REACT_APP_P_STUDIO_GROUP, identity, fetchUserInvites, refetchLicenseTransfers]);

  const invitationContextValue = useMemo(
    () => ({
      pendingInvitations,
      handleInvitation,
      fetchInvitations: fetchUserInvites,
      licenseTransfers,
      refetchLicenseTransfers,
    }),
    [fetchUserInvites, handleInvitation, pendingInvitations, licenseTransfers, refetchLicenseTransfers],
  );

  return <InvitationContext.Provider value={invitationContextValue}>{children}</InvitationContext.Provider>;
};
